import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { Button, Card, Icon, I, MOBILE_SINGLE_COLUMN, Pill, TEXT_WRAP_STYLE, mobileButtonStyle } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import {
  createWarehouse,
  deleteWarehouse,
  listWarehouses,
  updateWarehouse,
} from '../warehouses/warehousesApi'
import type { Warehouse, WarehousePayload } from '../warehouses/types'

interface FormState {
  code: string
  name: string
  location: string
}

const EMPTY_FORM: FormState = { code: '', name: '', location: '' }

function errorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `warehouses.errors.codes.${error.code}`
  }
  return 'warehouses.errors.codes.REQUEST_FAILED'
}

function MobileWarehouseField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
      <div style={{ color: 'var(--fg-subtle)', fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ color: 'var(--fg)', fontSize: 13, ...TEXT_WRAP_STYLE }}>{children}</div>
    </div>
  )
}

interface WarehousesPageProps {
  accessToken: string
}

export function WarehousesPage({ accessToken }: WarehousesPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)
  const [tableError, setTableError] = useState<unknown>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formError, setFormError] = useState<unknown>(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    // Intentional reset on accessToken change so the table shows a loading state while
    // re-fetching. The rule's general guidance (avoid cascading renders) doesn't fit this
    // canonical fetch-on-deps pattern.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    setError(null)

    listWarehouses(accessToken)
      .then(response => {
        if (!cancelled) setWarehouses(response)
      })
      .catch(caught => {
        if (!cancelled) setError(caught)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [accessToken])

  const rows = useMemo<Warehouse[]>(() => warehouses, [warehouses])

  const openCreate = () => {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setFormError(null)
    setFormOpen(true)
  }

  const openEdit = (warehouse: Warehouse) => {
    setEditingId(warehouse.id)
    setForm({
      code: warehouse.code,
      name: warehouse.name,
      location: warehouse.location ?? '',
    })
    setFormError(null)
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditingId(null)
    setForm(EMPTY_FORM)
    setFormError(null)
  }

  const submitForm = async () => {
    const code = form.code.trim()
    const name = form.name.trim()
    const location = form.location.trim()

    if (!code || !name) {
      setFormError(new ApiClientError(400, { code: 'VALIDATION_FAILED' }))
      return
    }

    const payload: WarehousePayload = {
      code,
      name,
      location: location ? location : null,
    }

    setSaving(true)
    setFormError(null)
    try {
      if (editingId == null) {
        const created = await createWarehouse(accessToken, payload)
        setWarehouses(prev => [created, ...prev])
      } else {
        const updated = await updateWarehouse(accessToken, editingId, payload)
        setWarehouses(prev => prev.map(w => (w.id === updated.id ? updated : w)))
      }
      closeForm()
    } catch (caught) {
      setFormError(caught)
    } finally {
      setSaving(false)
    }
  }

  const removeWarehouse = async (warehouse: Warehouse) => {
    const confirmText = String(t('warehouses.confirmDelete', { name: warehouse.name }))
    if (typeof window !== 'undefined' && !window.confirm(confirmText)) return

    setDeletingId(warehouse.id)
    setTableError(null)
    try {
      await deleteWarehouse(accessToken, warehouse.id)
      setWarehouses(prev => prev.filter(w => w.id !== warehouse.id))
    } catch (caught) {
      setTableError(caught)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div>
      <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', gap: 14, marginBottom: 18 }}>
        <p style={{ fontSize: 13.5, color: 'var(--fg-muted)', margin: 0, maxWidth: 600, ...TEXT_WRAP_STYLE }}>
          {t('warehouses.description.tlaaq')}
        </p>
        <div style={{ display: isMobile ? 'grid' : 'flex', gap: 8 }}>
          <Button kind="primary" size="sm" icon={I.plus} onClick={openCreate} style={mobileButtonStyle(isMobile)}>{t('warehouses.actions.new')}</Button>
        </div>
      </div>

      <Card padding={14} style={{
        background: 'var(--brand-100)',
        borderColor: 'var(--brand-300)',
        marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Icon size={16} color="var(--brand-700)">{I.shield}</Icon>
        <div style={{ flex: 1, fontSize: 12.5 }}>
          <span style={{ fontWeight: 700, color: 'var(--brand-700)' }}>
            {t('warehouses.banner.managed')}
          </span>
        </div>
      </Card>

      {formOpen && (
        <Card padding={20} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--brand-900)', marginBottom: 12 }}>
            {editingId == null ? t('warehouses.form.titleCreate') : t('warehouses.form.titleEdit')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
            <label style={{ display: 'block' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('warehouses.form.code')}</div>
              <input
                value={form.code}
                onChange={event => setForm(prev => ({ ...prev, code: event.target.value }))}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
                  color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                  borderRadius: 9, outline: 'none',
                }}
              />
            </label>
            <label style={{ display: 'block' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('warehouses.form.name')}</div>
              <input
                value={form.name}
                onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
                  color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                  borderRadius: 9, outline: 'none',
                }}
              />
            </label>
            <label style={{ display: 'block' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>{t('warehouses.form.location')}</div>
              <input
                value={form.location}
                onChange={event => setForm(prev => ({ ...prev, location: event.target.value }))}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
                  color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                  borderRadius: 9, outline: 'none',
                }}
              />
            </label>
          </div>
          {formError !== null && (
            <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5, fontWeight: 700 }}>
              {String(t(errorMessageKey(formError), { defaultValue: String(t('warehouses.errors.codes.REQUEST_FAILED')) }))}
            </div>
          )}
          <div style={{ display: isMobile ? 'grid' : 'flex', gap: 8, marginTop: 14 }}>
            <Button kind="primary" size="sm" onClick={submitForm} disabled={saving} style={mobileButtonStyle(isMobile)}>
              {saving ? t('warehouses.actions.saving') : t('warehouses.actions.save')}
            </Button>
            <Button kind="secondary" size="sm" onClick={closeForm} disabled={saving} style={mobileButtonStyle(isMobile)}>{t('warehouses.actions.cancel')}</Button>
          </div>
        </Card>
      )}

      {loading && (
        <Card padding={22} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--fg-muted)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.refresh}</Icon>
            {t('warehouses.loading')}
          </div>
        </Card>
      )}

      {error !== null && (
        <Card padding={22} style={{ marginBottom: 16, borderColor: 'var(--danger-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.alert}</Icon>
            {String(t(errorMessageKey(error), { defaultValue: String(t('warehouses.errors.codes.REQUEST_FAILED')) }))}
          </div>
        </Card>
      )}

      {tableError !== null && (
        <Card padding={18} style={{ marginBottom: 16, borderColor: 'var(--danger-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.alert}</Icon>
            {String(t(errorMessageKey(tableError), { defaultValue: String(t('warehouses.errors.codes.REQUEST_FAILED')) }))}
          </div>
        </Card>
      )}

      <Card padding={0}>
        {isMobile ? (
          <div style={{ display: 'grid' }}>
            {!loading && rows.length === 0 && (
              <div style={{ padding: '36px 22px', textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
                <div style={{ fontWeight: 700, color: 'var(--fg)', marginBottom: 4 }}>{t('warehouses.empty.title')}</div>
                <div>{t('warehouses.empty.body')}</div>
              </div>
            )}
            {rows.map(w => (
              <div key={w.id} style={{ display: 'grid', gap: 12, padding: 16, borderTop: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--brand-100)', color: 'var(--brand-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={16}>{I.warehouse}</Icon>
                  </div>
                  <div style={{ fontWeight: 700, color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>{w.name}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: MOBILE_SINGLE_COLUMN, gap: 10 }}>
                  <MobileWarehouseField label={t('warehouses.table.code')}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{w.code}</span>
                  </MobileWarehouseField>
                  <MobileWarehouseField label={t('warehouses.table.location')}>{w.location ?? '—'}</MobileWarehouseField>
                  <MobileWarehouseField label={t('warehouses.table.skus')}>{w.skuCount.toLocaleString(i18n.language)}</MobileWarehouseField>
                  <MobileWarehouseField label={t('warehouses.table.units')}>{w.unitCount.toLocaleString(i18n.language)}</MobileWarehouseField>
                  <MobileWarehouseField label={t('warehouses.table.source')}>
                    <Pill tone="brand" size="sm">Tlaaq</Pill>
                  </MobileWarehouseField>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    aria-label={String(t('warehouses.actions.edit'))}
                    onClick={() => openEdit(w)}
                    style={{ flex: 1, minWidth: 0, background: 'transparent', border: '1px solid var(--border-subtle)', height: 34, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--fg-muted)' }}
                  >
                    <Icon size={15}>{I.edit}</Icon>
                  </button>
                  <button
                    type="button"
                    aria-label={String(t('warehouses.actions.delete'))}
                    onClick={() => removeWarehouse(w)}
                    disabled={deletingId === w.id}
                    style={{ flex: 1, minWidth: 0, background: 'transparent', border: '1px solid var(--border-subtle)', height: 34, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: deletingId === w.id ? 'wait' : 'pointer', color: 'var(--fg-muted)' }}
                  >
                    <Icon size={15}>{I.trash}</Icon>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--ink-50)' }}>
                {[t('warehouses.table.warehouse'), t('warehouses.table.code'), t('warehouses.table.location'), t('warehouses.table.skus'), t('warehouses.table.units'), t('warehouses.table.source'), ''].map((h, index) => (
                  <th key={index} style={{ textAlign: 'start', padding: '11px 22px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: '36px 22px', textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
                    <div style={{ fontWeight: 700, color: 'var(--fg)', marginBottom: 4 }}>{t('warehouses.empty.title')}</div>
                    <div>{t('warehouses.empty.body')}</div>
                  </td>
                </tr>
              )}
              {rows.map(w => (
                <tr key={w.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '14px 22px' }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--brand-100)', color: 'var(--brand-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={16}>{I.warehouse}</Icon>
                      </div>
                      <div style={{ fontWeight: 700, color: 'var(--fg)' }}>{w.name}</div>
                    </div>
                  </td>
                  <td style={{ padding: '14px 22px', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-muted)' }}>{w.code}</td>
                  <td style={{ padding: '14px 22px', color: 'var(--fg-muted)' }}>{w.location ?? '—'}</td>
                  <td style={{ padding: '14px 22px', fontWeight: 700, color: 'var(--fg)' }}>{w.skuCount.toLocaleString(i18n.language)}</td>
                  <td style={{ padding: '14px 22px', fontWeight: 700, color: 'var(--fg)' }}>{w.unitCount.toLocaleString(i18n.language)}</td>
                  <td style={{ padding: '14px 22px' }}>
                    <Pill tone="brand" size="sm">Tlaaq</Pill>
                  </td>
                  <td style={{ padding: '14px 22px', textAlign: 'end' }}>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      <button
                        type="button"
                        aria-label={String(t('warehouses.actions.edit'))}
                        onClick={() => openEdit(w)}
                        style={{ background: 'transparent', border: '1px solid var(--border-subtle)', width: 28, height: 28, borderRadius: 7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--fg-muted)' }}
                      >
                        <Icon size={15}>{I.edit}</Icon>
                      </button>
                      <button
                        type="button"
                        aria-label={String(t('warehouses.actions.delete'))}
                        onClick={() => removeWarehouse(w)}
                        disabled={deletingId === w.id}
                        style={{ background: 'transparent', border: '1px solid var(--border-subtle)', width: 28, height: 28, borderRadius: 7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: deletingId === w.id ? 'wait' : 'pointer', color: 'var(--fg-muted)' }}
                      >
                        <Icon size={15}>{I.trash}</Icon>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  )
}
